import RouteService from "../service/routeService";
import RequestService from "../service/requestService";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { GraphQLClient } from "../../../../../shared/gql-client";
import {
  changeDriverForRoute,
  PlanningSliceModel,
  removeRoute,
  unsetRouteCustom,
} from "./PlanningSlice";
import { RouteModel } from "../../domain/models/routeModel";
import { getPlanningRoutes } from "./thunks/route/getPlanningRoutesThunk";

const routeApi = new RouteService();
const requestApi = new RequestService();

export const getRequestsFromDay = createAsyncThunk(
  "planning/get/day/routes",
  async (day: string | undefined, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      requestApi.setClient(client);

      if (!day) {
        const { planning } = thunkAPI.getState() as {
          planning: PlanningSliceModel;
        };
        day = planning.requestsDate;
      }

      return await requestApi.getRequestsFromDay(day);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const generateTruckLayout = createAsyncThunk(
  "planning/generate/truck/layout",
  async (id: string, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    routeApi.setClient(client);
    return await routeApi.generateTruckLayout(id);
  },
);

export const getExportTruckLayout = createAsyncThunk(
  "planning/get/exportTruckLayout",
  async ({ id }: { id: string }, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      routeApi.setClient(client);
      return await routeApi.exportTruckLayout(id);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const deleteRoute = createAsyncThunk(
  "planning/route/delete",
  async (route: RouteModel, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      routeApi.setClient(client);

      thunkAPI.dispatch(removeRoute(route));
      if (route.id.startsWith("state:")) {
        const response = await routeApi.getRouteFromData(route);
        if (response) {
          return await routeApi.delete(response);
        }
      } else {
        return await routeApi.delete(route.id);
      }
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const setRouteCustom = createAsyncThunk(
  "planning/route/set/custom",
  async (route: RouteModel, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      routeApi.setClient(client);

      thunkAPI.dispatch(
        unsetRouteCustom({
          route: route,
          custom: false,
        }),
      );

      if (route.id.startsWith("state:")) {
        const response = await routeApi.getRouteFromData(route);
        if (response) {
          return routeApi.update({
            id: {
              type: "ID!",
              value: response,
            },
            custom: {
              type: "Boolean",
              value: false,
            },
          });
        }
      } else {
        return routeApi.update({
          id: {
            type: "ID!",
            value: route.id,
          },
          custom: {
            type: "Boolean",
            value: false,
          },
        });
      }
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const updateDriverForRoute = createAsyncThunk(
  "planning/route/update/driver",
  async (
    { route, id, label }: { route: RouteModel; id: string; label: string },
    thunkAPI,
  ) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      routeApi.setClient(client);

      thunkAPI.dispatch(
        changeDriverForRoute({
          route: route,
          id: id,
          label: label,
        }),
      );

      if (route.id.startsWith("state:")) {
        const response = await routeApi.getRouteFromData(route);
        if (response) {
          return routeApi.update({
            id: {
              type: "ID!",
              value: response,
            },
            user_id: {
              type: "ID!",
              value: id,
            },
          });
        }
      } else {
        return routeApi.update({
          id: {
            type: "ID!",
            value: route.id,
          },
          user_id: {
            type: "ID!",
            value: id,
          },
        });
      }
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const updateLayoutForRoute = createAsyncThunk(
  "planning/route/update/layout",
  async (
    {
      id,
      layout,
      custom = false,
    }: { id: string; layout: string; custom?: boolean },
    thunkAPI,
  ) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      routeApi.setClient(client);

      return await routeApi.updateLayout({
        id: {
          type: "ID!",
          value: id,
        },
        layout: {
          type: "String",
          value: layout,
        },
        custom: {
          type: "Boolean",
          value: custom,
        },
      });
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const setRoutesDefinitive = createAsyncThunk(
  "planning/route/set/definitive",
  async (
    { routeIds, status }: { routeIds: string[]; status: string },
    thunkAPI,
  ) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      routeApi.setClient(client);

      const response = await routeApi.updateRoutesDefinitive(routeIds, status);

      if (response) {
        const { planning } = thunkAPI.getState() as {
          planning: PlanningSliceModel;
        };
        await thunkAPI.dispatch(getPlanningRoutes(planning.date));
      }

      return response;
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const generateRoutesFromDate = createAsyncThunk(
  "planning/route/generate/from/date",
  async (date: string, thunkAPI) => {
    const { client } = thunkAPI.extra as { client: GraphQLClient };
    try {
      routeApi.setClient(client);

      const response = await routeApi.generateRoutesFromDate(date);

      if (response) {
        await thunkAPI.dispatch(getPlanningRoutes(date));
      }

      return response;
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);
